import { Component, OnInit, Input, OnDestroy, ViewContainerRef, ViewChild } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MentionSearchModel } from '../../models/mention-search.model';
import { SearchRangeModel } from '../../../Core/models/search-range.model';
import { getMonitoringDateQueryInputTopicOptions } from '../../models/monitoring-date-query-input-options';
import { FolderService } from '../../../Core/services/folder.service';
import { Folder } from '../../models/folder.model';
import { TopicBackfillApi } from '../../../../api/backfills/topic-backfill.api';
import { TopicBackfillApiModel } from '../../../../api/backfills/models/topic-backfill.api.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DateFilterModel } from '../../../../Common/Forms/models/date-filter.model';
import { FolderBulkActionParameters } from '../BulkActionFormFolder/bulk-action-form-folder.component';
import { DateInputTypesEnum } from '../../../../Common/Forms/enums/date-input-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';


@Component({
    selector: 'agpr-topic-backfill-dialog',
    templateUrl: './topic-backfill-dialog.component.html',
    styleUrls: ['./topic-backfill-dialog.component.scss'],
    standalone: false
})
export class TopicBackfillDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() topic: MentionSearchModel;

    folderBulkActionParameters: FolderBulkActionParameters = {
        showAutoCreateOption: true
    };
    messageSeverityEnum = messageSeverityEnum;
    allowedRange: SearchRangeModel;
    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    selectedOption: DropdownOptionModel;

    dateQuery: DateQueryModel;

    selectedFolder: Folder;

    noFolderSelected: boolean = false;

    private dateQuerySubject: Subject<DateQueryModel> = new Subject();
    private dateQuerySub: Subscription;

    constructor(
        public coreDialogService: CoreDialogService,
        public folderService: FolderService,
        public topicBackfillApi: TopicBackfillApi,
        public messageService: MessageService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, 'TopicBackfill');
        this.allowedRange = new SearchRangeModel();
        this.allowedRange.to = new Date();
        this.allowedRange.from = moment().subtract(5, 'years').toDate();
        this.dateQuery = DateQueryModel.getLast24HoursQuery();

        this.selectedOption = this.dateFilterOptions.find(option => option.value.type === DateInputTypesEnum.custom);
    }

    ngOnInit(): void {
        this.dateQuerySub = this.dateQuerySubject.pipe(debounceTime(600)).subscribe(dateQuery => {
            this.dateQuery = dateQuery;
        });
    }

    getBackfillQuery(): TopicBackfillApiModel {
        const range = DateQueryModel.makeAbsoluteRange(this.dateQuery);
        const apiDate: DateFilterModel = {
            dateType: range.type,
            relativeStartOffsetH: range.relativeStartOffsetH,
            relativeEndOffsetH: range.relativeEndOffsetH,
            dateRange: range.absoluteRange
        };
        return {
            folderId: this.selectedFolder.id,
            dateFilter: apiDate,
            savedSearchId: this.topic.id
        };
    }

    updateDatequery(dateQuery: DateQueryModel) {
        this.dateQuerySubject.next(dateQuery);
    }

    folderSelected(folder: Folder) {
        this.selectedFolder = folder;
        this.noFolderSelected = !folder;
    }

    /**
     * Action when the user clicks the "OK" button (update when the backend is up)
     */
    ok(): void {
        if (this.selectedFolder) {
            this.topicBackfillApi.requestBackfill(this.getBackfillQuery()).subscribe({
                next: () => {
                    this.closeDialog(true);
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Monitoring.search.backfill.request.success.heading',
                        detail: this.localizationService.transform(
                            'Monitoring.search.backfill.request.success.message',
                            { name: this.topic.name }
                        )
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitoring.search.backfill.request.error.heading',
                        detail: this.localizationService.transform(
                            'Monitoring.search.backfill.request.error.message',
                            { name: this.topic.name }
                        )
                    });
                }
            });
        } else {
            this.noFolderSelected = true;
        }
    }

    ngOnDestroy(): void {
        if (this.dateQuerySub) {
            this.dateQuerySub.unsubscribe();
        }
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog(false);
    }
}
